import { useEffect, useState } from "react";
import {
  adminListPublicThemes,
  adminRegeneratePublicThemes,
  adminUpdatePublicThemes,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import {
  RefreshCw, Lock, Unlock, Eye, EyeOff, Save, Sparkles, ExternalLink, Info,
} from "lucide-react";

/**
 * Admin control page for the public "This week we heard from Catholics
 * about…" ticker on the home page. Staff can:
 *  - See the currently-published card + prior weeks
 *  - Force regenerate the current week (admin only)
 *  - Pause / re-activate any week
 *  - Lock a card so the nightly 2AM regen skips it
 *  - Edit theme labels (icon + link are re-derived server-side)
 */
export default function PublicThemes() {
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminListPublicThemes();
      setWeeks(r.themes || []);
    } catch { toast.error("Could not load themes"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const regenerate = async () => {
    setBusy(true);
    try {
      const r = await adminRegeneratePublicThemes();
      const kind = r.public_themes;
      if (kind === "locked") toast.info("This week is locked — unlock first.");
      else if (kind === "skipped") toast.info("Not enough traffic this week to regenerate.");
      else toast.success(`Regenerated ${kind} themes.`);
      load();
    } catch { toast.error("Regenerate failed"); }
    finally { setBusy(false); }
  };

  const toggleField = async (week, field, value) => {
    try {
      await adminUpdatePublicThemes(week.week_key, { [field]: value });
      toast.success(`${field} updated`);
      load();
    } catch { toast.error("Update failed"); }
  };

  const saveLabels = async (week, newThemes) => {
    try {
      await adminUpdatePublicThemes(week.week_key, { themes: newThemes });
      toast.success("Labels saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  return (
    <div data-testid="admin-public-themes-page">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-ccc-gold">Public site</div>
          <h1 className="font-serif text-4xl text-ccc-plum">Public Themes.</h1>
          <div className="text-sm text-stone-600 mt-1 max-w-2xl">
            Controls the "This week Catholics wrote us about&hellip;" strip on the home page.
            Nightly regen at 2 AM Denver &middot; requires 15+ visitor messages that week &middot; PII scrubbed before the LLM sees it.
          </div>
        </div>
        <Button onClick={regenerate} disabled={busy} data-testid="btn-regenerate">
          <RefreshCw size={14} className={`mr-1.5 ${busy ? "animate-spin" : ""}`}/>
          {busy ? "Regenerating…" : "Regenerate this week"}
        </Button>
      </div>

      {loading && <div className="text-stone-500 text-sm py-12 text-center">Loading…</div>}
      {!loading && weeks.length === 0 && (
        <div
          className="bg-white border border-stone-200 p-8 text-center text-stone-500 text-sm"
          data-testid="empty-themes"
        >
          <Info size={24} className="mx-auto mb-2 text-stone-400"/>
          No theme cards yet.<br/>
          The strip is currently hidden on the public site. Regenerate once you have 15+ visitor messages this week.
        </div>
      )}
      {!loading && weeks.map((w, wi) => (
        <WeekCard
          key={w.week_key}
          week={w}
          isCurrent={wi === 0}
          onToggleStatus={() => toggleField(w, "status", w.status === "active" ? "paused" : "active")}
          onToggleLock={() => toggleField(w, "locked", !w.locked)}
          onSaveLabels={(themes) => saveLabels(w, themes)}
        />
      ))}
    </div>
  );
}

function WeekCard({ week, isCurrent, onToggleStatus, onToggleLock, onSaveLabels }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(week.themes || []);

  useEffect(() => { setDraft(week.themes || []); }, [week]);

  const paused = week.status === "paused";
  return (
    <div
      className={`bg-white border p-6 mb-4 ${isCurrent ? "border-ccc-purple/40" : "border-stone-200"}`}
      data-testid={`week-card-${week.week_key}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <div className="font-serif text-xl text-ccc-plum">Week {week.week_key}</div>
            {isCurrent && <span className="text-[10px] uppercase tracking-widest bg-ccc-purple text-white px-2 py-0.5">Current</span>}
            {paused && <span className="text-[10px] uppercase tracking-widest bg-red-100 text-red-700 px-2 py-0.5">Paused</span>}
            {week.locked && <span className="text-[10px] uppercase tracking-widest bg-amber-100 text-amber-800 px-2 py-0.5">Locked</span>}
          </div>
          <div className="text-xs text-stone-500 mt-1">
            {week.message_count} messages &middot; generated{" "}
            {week.generated_at && formatDistanceToNow(new Date(week.generated_at), { addSuffix: true })}
            {week.updated_by && <> &middot; edited by {week.updated_by}</>}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline" size="sm" onClick={onToggleStatus}
            data-testid={`btn-toggle-status-${week.week_key}`}
          >
            {paused ? <><Eye size={14} className="mr-1.5"/>Show publicly</> : <><EyeOff size={14} className="mr-1.5"/>Pause</>}
          </Button>
          <Button
            variant="outline" size="sm" onClick={onToggleLock}
            data-testid={`btn-toggle-lock-${week.week_key}`}
          >
            {week.locked ? <><Unlock size={14} className="mr-1.5"/>Unlock</> : <><Lock size={14} className="mr-1.5"/>Lock</>}
          </Button>
          <Button
            variant={editing ? "default" : "outline"} size="sm"
            onClick={() => {
              if (editing) { onSaveLabels(draft); setEditing(false); }
              else setEditing(true);
            }}
            data-testid={`btn-edit-${week.week_key}`}
          >
            {editing ? <><Save size={14} className="mr-1.5"/>Save labels</> : <><Sparkles size={14} className="mr-1.5"/>Edit labels</>}
          </Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        {draft.map((t, ti) => (
          <div key={ti} className="border border-stone-200 p-4">
            {editing ? (
              <Input
                value={t.label}
                onChange={e => setDraft(prev => prev.map((x, xi) => xi === ti ? { ...x, label: e.target.value } : x))}
                className="mb-2 font-serif text-lg"
                data-testid={`edit-label-${week.week_key}-${ti}`}
              />
            ) : (
              <div className="font-serif text-lg text-ccc-plum leading-tight mb-2">{t.label}</div>
            )}
            <div className="text-xs text-stone-500 flex items-center gap-1">
              <span>{t.count} mention{t.count === 1 ? "" : "s"}</span>
              <span className="mx-1">&middot;</span>
              <span className="text-stone-400">{t.icon_key}</span>
              <a href={t.target_url} target="_blank" rel="noreferrer" className="ml-auto text-ccc-purple inline-flex items-center gap-0.5 hover:underline">
                <ExternalLink size={11}/>
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
